/**
 * Space Detail Page
 *
 * View space details, rooms, and admin controls
 */

import { type ScreenFormat, SourceType, Visibility } from '@polygraf/shared'
import {
	AlertCircle,
	ArrowLeft,
	Building,
	ChevronRight,
	Edit2,
	FileText,
	Folder,
	Globe,
	Lock,
	Monitor,
	Plus,
	Search,
	Trash2,
	Users,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
	buildRoomAclPayload,
	EditRoomModal,
	RoomVisibilityFields,
} from '@/components/room-edit-modal'
import { SpaceMembers } from '@/components/space-members'
import {
	Alert,
	Button,
	buttonClasses,
	cn,
	EmptyState,
	Modal,
	Select,
	Spinner,
	Textarea,
	TextField,
	Toggle,
} from '@/components/ui'
import { usePageTitle } from '@/hooks/use-page-title'
import { useSession } from '@/lib/auth-client'
import { parseLineList } from '@/routes/spaces'
import { trpc } from '@/trpc/client'

export default function SpaceDetailPage() {
	const { t } = useTranslation()
	const { spaceId } = useParams<{ spaceId: string }>()
	const navigate = useNavigate()
	useSession() // Keep auth context active
	const utils = trpc.useUtils()

	const {
		data: space,
		isLoading,
		error,
	} = trpc.spaces.get.useQuery(
		{ id: spaceId ?? '' },
		{ enabled: !!spaceId, refetchInterval: 5_000 },
	)
	usePageTitle(space?.name ?? t('spaces.title'))

	const [showCreateRoom, setShowCreateRoom] = useState(false)
	const [showEditSpace, setShowEditSpace] = useState(false)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [roomSearch, setRoomSearch] = useState('')

	const deleteSpaceMutation = trpc.spaces.delete.useMutation({
		onSuccess: () => {
			navigate('/spaces')
		},
	})

	if (isLoading) {
		return (
			<div className="flex justify-center py-12">
				<div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
			</div>
		)
	}

	if (error || !space) {
		return (
			<div className="space-y-4 animate-enter">
				<Link
					to="/spaces"
					className={cn(
						buttonClasses({ variant: 'ghost', size: 'sm' }),
						'flex items-center gap-2 w-fit',
					)}
				>
					<ArrowLeft size={18} />
					{t('spaceDetail.backToSpaces')}
				</Link>
				<div className="card-refined p-6 border-coral-500 bg-coral-500/5">
					<div className="flex items-center gap-3">
						<AlertCircle className="text-coral-500" size={24} />
						<div>
							<p className="font-semibold">{t('spaceDetail.notFound')}</p>
							<p className="text-sm text-base-content/60">{t('spaceDetail.notFoundDesc')}</p>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Permission to edit - server enforces actual permissions, this is for UI
	const canEdit = (space as { canManage?: boolean }).canManage ?? space.isOwner

	// Czech collation, case-insensitive — same reasoning as the spaces list (MU #7).
	const filteredRooms = space.rooms
		.filter((room) => {
			if (!roomSearch) return true
			const q = roomSearch.toLowerCase()
			return (
				room.name.toLowerCase().includes(q) ||
				(room.description?.toLowerCase().includes(q) ?? false)
			)
		})
		.sort((a, b) => a.name.localeCompare(b.name, 'cs', { sensitivity: 'base' }))

	const handleDeleteSpace = async () => {
		try {
			await deleteSpaceMutation.mutateAsync({ id: space.id })
		} catch {
			// Surfaced via deleteSpaceMutation.error in the confirm modal.
		}
	}

	return (
		<div className="space-y-6 animate-enter">
			{/* Header */}
			<div className="flex flex-col sm:flex-row justify-between items-start gap-4">
				<div className="flex items-center gap-4">
					<Link
						to="/spaces"
						className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2 rounded-xl')}
					>
						<ArrowLeft size={20} />
					</Link>
					<div>
						<div className="flex items-center gap-3">
							<h1 className="text-2xl font-bold flex items-center gap-2">
								<Folder size={24} className="text-primary flex-shrink-0" aria-hidden="true" />
								{space.name}
							</h1>
							<VisibilityBadge visibility={space.visibility} t={t} />
						</div>
						{space.description && <p className="text-base-content/60 mt-1">{space.description}</p>}
						<div className="flex items-center gap-4 mt-2 text-sm text-base-content/50">
							<span className="flex items-center gap-1">
								<Building size={14} />
								{t('spaceDetail.roomCount', { count: space.rooms.length })}
							</span>
							{space.memberCount > 0 && (
								<span className="flex items-center gap-1">
									<Users size={14} />
									{t('spaceDetail.memberCount', { count: space.memberCount })}
								</span>
							)}
						</div>
					</div>
				</div>

				{canEdit && (
					<div className="flex gap-2">
						<Button variant="ghost" size="sm" onClick={() => setShowEditSpace(true)}>
							<Edit2 size={16} />
							{t('common.edit')}
						</Button>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setShowDeleteConfirm(true)}
							disabled={deleteSpaceMutation.isPending}
							className="text-coral-700 hover:bg-coral-500/10"
						>
							{deleteSpaceMutation.isPending ? <Spinner size="sm" /> : <Trash2 size={16} />}
							{t('common.delete')}
						</Button>
					</div>
				)}
			</div>

			{/* Rooms Section */}
			<div className="space-y-4">
				<div className="flex justify-between items-center">
					<h2 className="text-lg font-semibold">{t('rooms.title')}</h2>
					{canEdit && (
						<Button onClick={() => setShowCreateRoom(true)}>
							<Plus size={16} />
							{t('spaceDetail.addRoom')}
						</Button>
					)}
				</div>

				{space.rooms.length === 0 ? (
					<div className="card-refined p-12 text-center">
						<div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-base-200 to-base-300 flex items-center justify-center">
							<Building size={32} className="text-base-content/30" />
						</div>
						<p className="text-lg font-medium mb-2">{t('rooms.noRooms')}</p>
						<p className="text-base-content/60">
							{canEdit ? t('spaceDetail.noRoomsOwner') : t('spaceDetail.noRoomsGuest')}
						</p>
					</div>
				) : (
					<>
						<TextField
							type="search"
							aria-label={t('spaceDetail.searchRoomsPlaceholder')}
							placeholder={t('spaceDetail.searchRoomsPlaceholder')}
							value={roomSearch}
							onChange={(e) => setRoomSearch(e.target.value)}
							leadingIcon={<Search size={18} aria-hidden />}
						/>

						{filteredRooms.length === 0 ? (
							<div className="card-refined">
								<EmptyState
									icon={<Search size={28} />}
									title={t('spaceDetail.noRoomsMatch')}
									description={t('spaceDetail.noRoomsMatchHint')}
								/>
							</div>
						) : (
							<div className="space-y-3">
								{filteredRooms.map((room, index) => (
									<RoomCard
										key={room.id}
										room={room}
										spaceId={space.id}
										canEdit={canEdit}
										index={index}
										onDelete={() => utils.spaces.get.invalidate({ id: space.id })}
										t={t}
									/>
								))}
							</div>
						)}
					</>
				)}
			</div>

			{/* Members Section (visible to space managers) */}
			{canEdit && <SpaceMembers spaceId={space.id} />}

			{/* Create Room Modal */}
			{showCreateRoom && (
				<CreateRoomModal
					spaceId={space.id}
					onClose={() => setShowCreateRoom(false)}
					onSuccess={() => {
						setShowCreateRoom(false)
						utils.spaces.get.invalidate({ id: space.id })
					}}
					t={t}
				/>
			)}

			{/* Edit Space Modal */}
			{showEditSpace && (
				<EditSpaceModal
					space={space}
					onClose={() => setShowEditSpace(false)}
					onSuccess={() => {
						setShowEditSpace(false)
						utils.spaces.get.invalidate({ id: space.id })
					}}
					t={t}
				/>
			)}

			{/* Delete Space confirmation */}
			<Modal
				open={showDeleteConfirm}
				onClose={() => setShowDeleteConfirm(false)}
				title={t('spaceDetail.confirmDeleteSpaceTitle')}
				closeLabel={t('common.close')}
				size="sm"
				persistent
				footer={
					<>
						<Button type="button" variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
							{t('common.cancel')}
						</Button>
						<Button
							type="button"
							variant="danger"
							loading={deleteSpaceMutation.isPending}
							onClick={handleDeleteSpace}
						>
							{t('common.delete')}
						</Button>
					</>
				}
			>
				<p className="text-sm text-base-content/70">{t('spaceDetail.confirmDeleteSpace')}</p>
				{deleteSpaceMutation.error && (
					<Alert tone="error" className="mt-4">
						{deleteSpaceMutation.error.message}
					</Alert>
				)}
			</Modal>
		</div>
	)
}

function VisibilityBadge({
	visibility,
	t,
}: {
	visibility: Visibility
	t: (key: string) => string
}) {
	const styles = {
		[Visibility.PUBLIC]: {
			bg: 'bg-sage-500/10',
			// F-010: sage[500] (#5d8a6a) on sage-tinted bg = 3.53:1, fails WCAG 1.4.3.
			// sage[600] passes ~6:1 on the same bg.
			text: 'text-sage-600',
			icon: Globe,
			labelKey: 'spaces.visibility.public',
		},
		[Visibility.AUTHENTICATED]: {
			bg: 'bg-primary/10',
			text: 'text-primary',
			icon: Users,
			labelKey: 'spaces.visibility.authenticated',
		},
		[Visibility.PRIVATE]: {
			bg: 'bg-coral-500/10',
			text: 'text-coral-700',
			icon: Lock,
			labelKey: 'spaces.visibility.private',
		},
		[Visibility.IP_RESTRICTED]: {
			bg: 'bg-base-300',
			text: 'text-base-content/70',
			icon: Globe,
			labelKey: 'spaces.visibility.ipRestricted',
		},
	}

	const style = styles[visibility]
	if (!style) return null

	const Icon = style.icon

	return (
		<div
			className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${style.bg} ${style.text}`}
		>
			<Icon size={12} />
			{t(style.labelKey)}
		</div>
	)
}

interface RoomData {
	id: string
	name: string
	description?: string | null
	isActive: boolean
	visibility?: Visibility
	inheritAccess?: boolean
	allowedIpRanges?: string[]
	allowedDomains?: string[]
	services: Array<{
		id: string
		type: SourceType | string
		name: string
		format?: ScreenFormat | string | null
		isActive: boolean
		isLive?: boolean
		/** "Sending right now", as opposed to `isLive` = "a publisher is
		 *  attached". Optional so an older server keeps the old behaviour. */
		isPublishing?: boolean
	}>
}

function RoomCard({
	room,
	spaceId,
	canEdit,
	index,
	onDelete,
	t,
}: {
	room: RoomData
	spaceId: string
	canEdit: boolean
	index: number
	onDelete: () => void
	t: (key: string, options?: Record<string, unknown>) => string
}) {
	const [showEdit, setShowEdit] = useState(false)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

	const deleteRoomMutation = trpc.rooms.delete.useMutation({
		onSuccess: onDelete,
	})

	const handleDelete = async () => {
		try {
			await deleteRoomMutation.mutateAsync({ id: room.id })
		} catch {
			// Surfaced via deleteRoomMutation.error in the confirm modal.
		}
	}

	const transcriptServices = room.services.filter((s) => s.type === SourceType.TRANSCRIPT)
	// "Prezentační obraz" is one service type to the user now (MU 2026-07-24
	// #6): SCREEN plus legacy SCREENCAST rows counted together.
	const imageServices = room.services.filter(
		(s) => s.type === SourceType.SCREEN || s.type === SourceType.SCREENCAST,
	)
	// The red "N živě" badge must count only services that are actually
	// SENDING. `isLive` now means "a transcriber is attached", so counting it
	// here would light the badge for a writer who paired and never typed —
	// re-creating the badge MU asked us to remove (Sváťa, 11. 4.: it implied
	// something was streaming when nothing was) and contradicting the room
	// tile one click deeper, which reads "Připojen, nepíše". Mirrors
	// packages/mobile/app/space/[id].tsx.
	const liveCount = room.services.filter((s) => s.isPublishing ?? s.isLive).length

	return (
		<>
			{/* `relative` + the overlay Link below make the WHOLE card open the
			    room (MU 5. 8.: spaces open on block click, rooms only via the
			    button — unify). A dedicated overlay anchor, NOT a ::before
			    stretched off the Otevřít button: buttonClasses carries
			    `active:scale-[0.98]`, and that :active transform turned the
			    button into the pseudo's containing block — on mousedown the
			    click surface collapsed under the cursor, so clicks never
			    completed and the cursor flickered (MU 18. 8.). Actions sit
			    above the overlay via z-10. */}
			<div className={`card-refined relative p-4 animate-enter stagger-${Math.min(index + 1, 5)}`}>
				<Link
					to={`/spaces/${spaceId}/rooms/${room.id}`}
					className="absolute inset-0 z-0 rounded-2xl"
					aria-hidden="true"
					tabIndex={-1}
				/>
				<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
					<div className="flex items-start gap-4 min-w-0">
						<div className="w-12 h-12 rounded-xl bg-gradient-to-br from-base-200 to-base-300 flex items-center justify-center flex-shrink-0">
							<Building size={24} className="text-primary" />
						</div>

						<div className="flex-1 min-w-0">
							{/* The room tiles do NOT overflow the way the space tiles did
							    (measured: 0-1px at 320/360/390) — the name has no `truncate`,
							    so nothing is lost. What they do share is the squeeze: the
							    non-wrapping title row let the "Neaktivní" pill crush a long
							    Czech name into a ~115px column, 5 lines tall at 320px. With
							    the row wrapping the same name gets ~190px and 3 lines. */}
							<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
								<h2 className="font-semibold text-lg min-w-0 break-words">{room.name}</h2>
								{!room.isActive && (
									<span className="px-2 py-0.5 text-xs rounded-full bg-base-300 text-base-content/60">
										{t('spaceDetail.inactive')}
									</span>
								)}
							</div>

							{/* Services summary */}
							<div className="flex flex-wrap gap-2 mt-3">
								{transcriptServices.length > 0 && (
									<span className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-primary/10 text-primary">
										<FileText size={12} />
										{t('spaceDetail.transcriptCount', { count: transcriptServices.length })}
									</span>
								)}
								{imageServices.length > 0 && (
									<span className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-sage-500/10 text-sage-600">
										<Monitor size={12} />
										{t('spaceDetail.imageCount', { count: imageServices.length })}
									</span>
								)}
								{room.services.length === 0 && (
									<span className="text-xs text-base-content/40">
										{t('spaceDetail.noServices')}
									</span>
								)}
								{liveCount > 0 && (
									<span className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-error/10 text-coral-700">
										<span className="w-1.5 h-1.5 rounded-full bg-error animate-pulse" />
										{t('spaceDetail.liveBadge', { count: liveCount })}
									</span>
								)}
							</div>
						</div>
					</div>

					<div className="flex items-center gap-2 flex-shrink-0">
						{canEdit && (
							<>
								{/* z-10 keeps edit/delete above the card-wide stretched Open
								    link, so they stay clickable. */}
								<button
									type="button"
									onClick={() => setShowEdit(true)}
									className={cn(
										buttonClasses({ variant: 'ghost', size: 'sm' }),
										'relative z-10 p-2 rounded-lg',
									)}
									aria-label={t('common.edit')}
								>
									<Edit2 size={16} />
								</button>
								<button
									type="button"
									onClick={() => setShowDeleteConfirm(true)}
									disabled={deleteRoomMutation.isPending}
									className={cn(
										buttonClasses({ variant: 'ghost', size: 'sm' }),
										'relative z-10 p-2 rounded-lg text-coral-700',
									)}
									aria-label={t('common.delete')}
								>
									{deleteRoomMutation.isPending ? <Spinner size="sm" /> : <Trash2 size={16} />}
								</button>
							</>
						)}
						<Link
							to={`/spaces/${spaceId}/rooms/${room.id}`}
							// The visible, keyboard-accessible way in; z-10 lifts it above
							// the card-wide overlay Link so the two never fight.
							className={cn(buttonClasses(), 'relative z-10 inline-flex items-center gap-1')}
						>
							{t('spaceDetail.open')}
							<ChevronRight size={16} />
						</Link>
					</div>
				</div>
			</div>

			{showEdit && (
				<EditRoomModal
					room={room}
					onClose={() => setShowEdit(false)}
					onSuccess={() => {
						setShowEdit(false)
						onDelete() // Refresh
					}}
					t={t}
				/>
			)}

			{/* Delete Room confirmation */}
			<Modal
				open={showDeleteConfirm}
				onClose={() => setShowDeleteConfirm(false)}
				title={t('spaceDetail.confirmDeleteRoomTitle')}
				closeLabel={t('common.close')}
				size="sm"
				persistent
				footer={
					<>
						<Button type="button" variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
							{t('common.cancel')}
						</Button>
						<Button
							type="button"
							variant="danger"
							loading={deleteRoomMutation.isPending}
							onClick={handleDelete}
						>
							{t('common.delete')}
						</Button>
					</>
				}
			>
				<p className="text-sm text-base-content/70">{t('spaceDetail.confirmDeleteRoom')}</p>
				{deleteRoomMutation.error && (
					<Alert tone="error" className="mt-4">
						{deleteRoomMutation.error.message}
					</Alert>
				)}
			</Modal>
		</>
	)
}

// Modal Components
function CreateRoomModal({
	spaceId,
	onClose,
	onSuccess,
	t,
}: {
	spaceId: string
	onClose: () => void
	onSuccess: () => void
	t: (key: string) => string
}) {
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	// New rooms inherit the space's permissions by default (matches the
	// server-side default — UAT F2, MP-217).
	const [inheritAccess, setInheritAccess] = useState(true)
	const [visibility, setVisibility] = useState<Visibility>(Visibility.PUBLIC)
	const [allowedIpRanges, setAllowedIpRanges] = useState('')
	const [allowedDomains, setAllowedDomains] = useState('')
	const [isActive, setIsActive] = useState(true)
	const [submitting, setSubmitting] = useState(false)

	const createRoomMutation = trpc.rooms.create.useMutation()

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setSubmitting(true)
		try {
			await createRoomMutation.mutateAsync({
				spaceId,
				name,
				description: description || undefined,
				isActive,
				...buildRoomAclPayload(inheritAccess, visibility, allowedIpRanges, allowedDomains),
			})
			onSuccess()
		} catch {
			// Surfaced via createRoomMutation.error below.
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Modal
			open
			onClose={onClose}
			title={t('spaceDetail.modal.newRoom')}
			closeLabel={t('common.close')}
			size="md"
			footer={
				<>
					<Button type="button" variant="ghost" onClick={onClose}>
						{t('common.cancel')}
					</Button>
					<Button type="submit" form="create-room-form" loading={submitting} disabled={!name}>
						{t('common.create')}
					</Button>
				</>
			}
		>
			<form id="create-room-form" onSubmit={handleSubmit} className="space-y-4">
				<TextField
					type="text"
					label={t('spaceDetail.modal.roomName')}
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder={t('spaceDetail.modal.roomNamePlaceholder')}
					required
					autoFocus
				/>

				<Textarea
					label={t('spaceDetail.modal.description')}
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					placeholder={t('spaceDetail.modal.descriptionPlaceholder')}
				/>

				<Toggle
					label={t('spaceDetail.modal.active')}
					checked={isActive}
					onChange={(e) => setIsActive(e.target.checked)}
				/>

				<RoomVisibilityFields
					inheritAccess={inheritAccess}
					setInheritAccess={setInheritAccess}
					visibility={visibility}
					setVisibility={setVisibility}
					allowedIpRanges={allowedIpRanges}
					setAllowedIpRanges={setAllowedIpRanges}
					allowedDomains={allowedDomains}
					setAllowedDomains={setAllowedDomains}
					t={t}
				/>

				{createRoomMutation.error && <Alert tone="error">{createRoomMutation.error.message}</Alert>}
			</form>
		</Modal>
	)
}

function EditSpaceModal({
	space,
	onClose,
	onSuccess,
	t,
}: {
	space: {
		id: string
		name: string
		description?: string | null
		visibility: Visibility
		allowedIpRanges?: string[]
		allowedDomains?: string[]
	}
	onClose: () => void
	onSuccess: () => void
	t: (key: string) => string
}) {
	const [name, setName] = useState(space.name)
	const [description, setDescription] = useState(space.description || '')
	const [visibility, setVisibility] = useState(space.visibility)
	const [allowedIpRanges, setAllowedIpRanges] = useState((space.allowedIpRanges ?? []).join('\n'))
	const [allowedDomains, setAllowedDomains] = useState((space.allowedDomains ?? []).join('\n'))
	const [submitting, setSubmitting] = useState(false)

	const updateSpaceMutation = trpc.spaces.update.useMutation({
		onSuccess,
	})

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setSubmitting(true)
		try {
			const isIpRestricted = visibility === Visibility.IP_RESTRICTED
			// Which tiers the server actually consults (evaluateSpaceAccess):
			//   - allowedDomains: PRIVATE (wildcard-domain grant, MP-216) AND IP_RESTRICTED
			//   - allowedIpRanges: IP_RESTRICTED only
			// Persist each only for the tiers that use it, and clear it otherwise so
			// switching to a non-consulting tier can't resurrect stale rules. Before
			// this, a PRIVATE space's wildcard domains were silently wiped on every
			// edit even though the server still grants access by them.
			const consultsDomains = isIpRestricted || visibility === Visibility.PRIVATE
			await updateSpaceMutation.mutateAsync({
				id: space.id,
				name,
				description: description || null,
				visibility,
				allowedIpRanges: isIpRestricted ? parseLineList(allowedIpRanges) : [],
				allowedDomains: consultsDomains ? parseLineList(allowedDomains) : [],
			})
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Modal
			open
			onClose={onClose}
			title={t('spaceDetail.modal.editSpace')}
			closeLabel={t('common.close')}
			size="md"
			footer={
				<>
					<Button type="button" variant="ghost" onClick={onClose}>
						{t('common.cancel')}
					</Button>
					<Button type="submit" form="edit-space-form" loading={submitting} disabled={!name}>
						{t('common.save')}
					</Button>
				</>
			}
		>
			<form id="edit-space-form" onSubmit={handleSubmit} className="space-y-4">
				<TextField
					type="text"
					label={t('spaceDetail.modal.name')}
					value={name}
					onChange={(e) => setName(e.target.value)}
					required
					autoFocus
				/>

				<Textarea
					label={t('spaceDetail.modal.description')}
					value={description}
					onChange={(e) => setDescription(e.target.value)}
				/>

				<Select
					label={t('spaceDetail.modal.visibility')}
					value={visibility}
					onChange={(e) => setVisibility(e.target.value as Visibility)}
				>
					<option value={Visibility.PUBLIC}>{t('spaces.visibility.public')}</option>
					<option value={Visibility.AUTHENTICATED}>{t('spaces.visibility.authenticated')}</option>
					<option value={Visibility.PRIVATE}>{t('spaces.visibility.private')}</option>
					<option value={Visibility.IP_RESTRICTED}>{t('spaces.visibility.ipRestricted')}</option>
				</Select>

				{visibility === Visibility.IP_RESTRICTED && (
					<Textarea
						label={t('spaces.createModal.allowedIpRangesLabel')}
						value={allowedIpRanges}
						onChange={(e) => setAllowedIpRanges(e.target.value)}
						placeholder={t('spaces.createModal.allowedIpRangesPlaceholder')}
						rows={3}
					/>
				)}

				{/* Wildcard email domains grant PRIVATE spaces too (MP-216), not just
				    IP_RESTRICTED — the server consults space.allowedDomains for both.
				    Hiding this for PRIVATE silently wiped the domains on every save. */}
				{(visibility === Visibility.IP_RESTRICTED || visibility === Visibility.PRIVATE) && (
					<>
						<Textarea
							label={t('spaces.createModal.allowedDomainsLabel')}
							value={allowedDomains}
							onChange={(e) => setAllowedDomains(e.target.value)}
							placeholder={t('spaces.createModal.allowedDomainsPlaceholder')}
							rows={2}
						/>
						<p className="text-xs text-base-content/60">
							{t('spaces.createModal.restrictionsHint')}
						</p>
					</>
				)}

				{updateSpaceMutation.error && (
					<Alert tone="error">{updateSpaceMutation.error.message}</Alert>
				)}
			</form>
		</Modal>
	)
}
